import { FC, useCallback } from 'react';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import { useDispatch, useStore } from 'react-redux';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NavItem } from 'src/types/NavBarTypes';
import { setBottomNavHeight } from 'src/redux/slices/NewRenterInterface/NewRentersInterfaceUIManagementSlice';
import { RootState } from 'src/redux/rootReducerType';

export const NAV_BADGE_SX = {
  '& .MuiBadge-badge': {
    fontSize: 10,
    height: 16,
    minWidth: 16,
    fontWeight: 700,
    bgcolor: 'error.dark',
  },
} as const;

interface RenterNavBarMobileProps {
  navItems: NavItem[];
  activeIndex: number;
  onNavigate: (path: string, external?: boolean) => void;
}

const RenterNavBarMobile: FC<RenterNavBarMobileProps> = ({
  navItems,
  activeIndex,
  onNavigate,
}) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const bottomNavRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) {
        return;
      }
      const height = el.getBoundingClientRect().height;
      const current = store.getState().newRenterUI.bottomNavHeight;
      if (current !== height) {
        dispatch(setBottomNavHeight(height));
      }
    },
    [dispatch, store]
  );

  return (
    <Paper
      ref={bottomNavRefCallback}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        boxShadow: (theme) => `0 -2px 4px ${theme.palette.divider}`,
        zIndex: 2,
      }}
      elevation={3}
    >
      <BottomNavigation
        showLabels
        value={activeIndex === -1 ? false : activeIndex}
        onChange={(_, newValue: number) => {
          const item = navItems[newValue];
          onNavigate(item.path, item.external);
        }}
        sx={{
          '& .MuiBottomNavigationAction-root': {
            // Five destinations must fit a 390px phone without clipping labels,
            // and still fit a 320px one with smaller type.
            minWidth: 56,
            px: 0.5,
          },
          '& .Mui-selected': {
            fontSize: '12px !important',
          },
          '& .MuiBottomNavigationAction-label': {
            whiteSpace: 'nowrap',
            fontSize: 11,
            '@media (max-width: 359px)': { fontSize: 9.5 },
          },
        }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.label}
            label={item.label}
            aria-label={
              item.dotBadge
                ? `${item.label}, action required`
                : item.badge
                ? `${item.label}, ${item.badge} pending`
                : item.label
            }
            icon={
              <div style={{ position: 'relative' }}>
                <Badge
                  badgeContent={item.dotBadge ? undefined : item.badge ?? 0}
                  variant={item.dotBadge ? 'dot' : 'standard'}
                  invisible={item.dotBadge ? false : !(item.badge ?? 0)}
                  color="error"
                  max={99}
                  sx={NAV_BADGE_SX}
                >
                  {item.icon}
                </Badge>
                {item.external && (
                  <OpenInNewIcon
                    color="info"
                    sx={{
                      fontSize: 12,
                      position: 'absolute',
                      top: '0%',
                      right: '-50%',
                    }}
                  />
                )}
              </div>
            }
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default RenterNavBarMobile;
